import { useState, useEffect, useCallback } from 'react';
import type {
  Agent,
  ContainmentStatus,
  BondSummary,
  SlashingEvent,
  Reputation,
  VerticalSummary,
  Attestation,
  QuarantinedAgent,
  AppealHistory,
  MetabolicSnapshot,
} from '../types';

export interface ClawbookDataState {
  containment: ContainmentStatus;
  healthHistory: MetabolicSnapshot[];
  bonds: BondSummary;
  slashingEvents: SlashingEvent[];
  hopsEarnings: number;
  agents: Agent[];
  reputation: Reputation;
  verticals: VerticalSummary[];
  attestations: Attestation[];
  quarantined: QuarantinedAgent[];
  appealHistory: AppealHistory[];
}

export interface UseClawbookDataOptions {
  apiUrl?: string;
  apiKey?: string;
  operatorDid: string;
  refreshInterval?: number;
}

export interface ClawbookActions {
  spawnAgent: (agentDid: string) => Promise<boolean>;
  pauseAgent: (agentDid: string) => Promise<boolean>;
  resumeAgent: (agentDid: string) => Promise<boolean>;
  terminateAgent: (agentDid: string) => Promise<boolean>;
  stakeAgent: (agentDid: string, amount: number) => Promise<boolean>;
}

export interface UseClawbookDataResult {
  data: ClawbookDataState | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  actions: ClawbookActions;
}

const DEFAULT_API_URL = 'https://api.clawbook.io';

/**
 * Hook for fetching Clawbook Control Tower data
 */
export function useClawbookData({
  apiUrl = DEFAULT_API_URL,
  apiKey,
  operatorDid,
  refreshInterval = 30000,
}: UseClawbookDataOptions): UseClawbookDataResult {
  const [data, setData] = useState<ClawbookDataState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Claw-Operator-DID': operatorDid,
      };

      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      // Fetch all data in parallel
      const [
        containmentRes,
        bondsRes,
        agentsRes,
        marketplaceRes,
        quarantineRes,
      ] = await Promise.all([
        fetch(`${apiUrl}/v1/containment/status`, { headers }),
        fetch(`${apiUrl}/v1/bonds/summary`, { headers }),
        fetch(`${apiUrl}/v1/agents`, { headers }),
        fetch(`${apiUrl}/v1/marketplace/summary`, { headers }),
        fetch(`${apiUrl}/v1/quarantine`, { headers }),
      ]);

      // Check for errors
      if (!containmentRes.ok) throw new Error('Failed to fetch containment status');
      if (!bondsRes.ok) throw new Error('Failed to fetch bonds');
      if (!agentsRes.ok) throw new Error('Failed to fetch agents');
      if (!marketplaceRes.ok) throw new Error('Failed to fetch marketplace');
      if (!quarantineRes.ok) throw new Error('Failed to fetch quarantine');

      // Parse responses
      const containmentData = await containmentRes.json();
      const bondsData = await bondsRes.json();
      const agentsData = await agentsRes.json();
      const marketplaceData = await marketplaceRes.json();
      const quarantineData = await quarantineRes.json();

      setData({
        containment: containmentData.status,
        healthHistory: containmentData.healthHistory || [],
        bonds: bondsData.summary,
        slashingEvents: bondsData.slashingEvents || [],
        hopsEarnings: bondsData.hopsEarnings || 0,
        agents: agentsData.agents || [],
        reputation: marketplaceData.reputation,
        verticals: marketplaceData.verticals || [],
        attestations: marketplaceData.attestations || [],
        quarantined: quarantineData.agents || [],
        appealHistory: quarantineData.appealHistory || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, apiKey, operatorDid]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  // Action helpers
  const makeAction = useCallback(
    async (endpoint: string, method: string = 'POST', body?: unknown): Promise<boolean> => {
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Claw-Operator-DID': operatorDid,
        };

        if (apiKey) {
          headers['Authorization'] = `Bearer ${apiKey}`;
        }

        const response = await fetch(`${apiUrl}${endpoint}`, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
          return false;
        }

        // Refresh data after action
        await fetchData();
        return true;
      } catch {
        return false;
      }
    },
    [apiUrl, apiKey, operatorDid, fetchData]
  );

  const actions: ClawbookActions = {
    spawnAgent: useCallback(
      (agentDid: string) => makeAction(`/v1/agents/${encodeURIComponent(agentDid)}/spawn`),
      [makeAction]
    ),
    pauseAgent: useCallback(
      (agentDid: string) => makeAction(`/v1/agents/${encodeURIComponent(agentDid)}/pause`),
      [makeAction]
    ),
    resumeAgent: useCallback(
      (agentDid: string) => makeAction(`/v1/agents/${encodeURIComponent(agentDid)}/resume`),
      [makeAction]
    ),
    terminateAgent: useCallback(
      (agentDid: string) => makeAction(`/v1/agents/${encodeURIComponent(agentDid)}/terminate`),
      [makeAction]
    ),
    stakeAgent: useCallback(
      (agentDid: string, amount: number) =>
        makeAction(`/v1/agents/${encodeURIComponent(agentDid)}/stake`, 'POST', { amount }),
      [makeAction]
    ),
  };

  return {
    data,
    loading,
    error,
    refresh: fetchData,
    actions,
  };
}

/**
 * Generate mock data for development/testing
 */
export function useMockClawbookData(): ClawbookDataState {
  const now = new Date().toISOString();

  return {
    containment: {
      activeVms: 12,
      totalVms: 15,
      networkEscapes: 0,
      cpuUsage: 43,
      ramUsage: 62,
      isolationScore: 100,
      lastUpdated: now,
    },
    healthHistory: Array.from({ length: 48 }, (_, i) => ({
      timestamp: new Date(Date.now() - i * 30 * 60 * 1000).toISOString(),
      cpu: 30 + Math.random() * 40,
      ram: 40 + Math.random() * 30,
      withinLimits: true,
    })),
    bonds: {
      totalLocked: 15500,
      tier2Bonds: 2500,
      tier2Count: 5,
      tier3Bonds: 13000,
      tier3Count: 2,
      availableBalance: 500,
    },
    slashingEvents: [
      {
        id: 'slash-1',
        agentDid: 'did:claw:7x92b',
        reason: 'RESOURCE_EXHAUST',
        amount: 50,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        appealed: false,
      },
      {
        id: 'slash-2',
        agentDid: 'did:claw:4k31a',
        reason: 'MINOR_BREACH',
        amount: 25,
        timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        appealed: true,
        appealStatus: 'granted',
      },
    ],
    hopsEarnings: 1247,
    agents: [
      {
        did: 'did:claw:7x92b',
        name: 'HVACScheduler',
        status: 'active',
        tier: 2,
        level: 5,
        cpu: 23,
        ramMb: 128,
        uptime: 47,
        tasksCompleted: 312,
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        lastHeartbeat: now,
      },
      {
        did: 'did:claw:4k31a',
        name: 'InvoiceAuditor',
        status: 'active',
        tier: 3,
        level: 7,
        cpu: 45,
        ramMb: 256,
        uptime: 123,
        tasksCompleted: 89,
        createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        lastHeartbeat: now,
      },
      {
        did: 'did:claw:9m72c',
        name: 'TestBot',
        status: 'sandbox',
        tier: 0,
        level: 1,
        cpu: 0,
        ramMb: 0,
        uptime: 0,
        tasksCompleted: 0,
        createdAt: now,
        lastHeartbeat: now,
      },
    ],
    reputation: {
      level: 6,
      score: 0.78,
      tasksCompleted: 401,
      successRate: 0.97,
      avgResponseTime: 2.3,
    },
    verticals: [
      { vertical: 'hvac', taskCount: 47, rewardRange: [12, 45] },
      { vertical: 'healthcare', taskCount: 12, rewardRange: [80, 400], compliance: 'HIPAA required' },
      { vertical: 'finance', taskCount: 8, rewardRange: [200, 1000], compliance: 'SEC required' },
    ],
    attestations: [
      {
        taskId: 'task-xyz789',
        agentDid: 'did:claw:7x92b',
        outputHash: '0x83f1a2b3c4d5e6f7',
        reward: 45,
        completedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        verified: true,
      },
      {
        taskId: 'task-abc123',
        agentDid: 'did:claw:4k31a',
        outputHash: '0x91a2b3c4d5e6f7g8',
        reward: 120,
        completedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        verified: true,
      },
    ],
    quarantined: [],
    appealHistory: [
      {
        id: 'appeal-1',
        agentDid: 'did:claw:2x41q',
        reason: 'MARKETPLACE_FRAUD',
        outcome: 'granted',
        bondRefunded: 475,
        validatorVotes: '2/3 voted System',
        resolvedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  };
}
